/**
 * BlocoSemaforo — "Semáforo da guarita" em Configurações → Integração (Onda 141)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * O que as chaves genéricas não cobrem: a senha do broker (gravada cifrada, nunca
 * devolvida), o estado da conexão e as controladoras que estão dando sinal de vida
 * (heartbeat a cada 30 s). O liga/desliga por câmera fica na tela Câmeras.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import '../styles/BlocoSemaforo.css';

const haQuanto = (iso) => {
  if (!iso) return 'nunca';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 90) return `há ${s} s`;
  if (s < 3600) return `há ${Math.round(s / 60)} min`;
  return `há ${Math.round(s / 3600)} h`;
};

export default function BlocoSemaforo() {
  const [estado, setEstado] = useState(null);
  const [falhou, setFalhou] = useState(false);
  const [senha, setSenha] = useState('');
  const [senhaControladora, setSenhaControladora] = useState(''); // Onda 141b: vai no config.h do firmware
  const [gravando, setGravando] = useState(false);
  const [gravandoControladora, setGravandoControladora] = useState(false);
  const [reconectando, setReconectando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/api/semaforo/estado');
      setEstado(r.data);
      setFalhou(false);
    } catch (_) {
      setFalhou(true);
    }
  }, []);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 15000);
    return () => clearInterval(t);
  }, [carregar]);

  const gravarSenha = async () => {
    setGravando(true);
    try {
      await api.post('/api/semaforo/senha', { senha });
      toast.success(senha ? 'Senha do broker gravada.' : 'Senha do broker apagada.');
      setSenha('');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível gravar a senha');
    } finally {
      setGravando(false);
    }
  };

  const gravarSenhaControladora = async () => {
    setGravandoControladora(true);
    try {
      await api.post('/api/semaforo/senha', { senha: senhaControladora, alvo: 'controladora' });
      toast.success(senhaControladora ? 'Senha da controladora gravada: o firmware baixado na Central de Ajuda já sai com ela.' : 'Senha da controladora apagada.');
      setSenhaControladora('');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível gravar a senha da controladora');
    } finally {
      setGravandoControladora(false);
    }
  };

  const reconectar = async () => {
    setReconectando(true);
    try {
      const r = await api.post('/api/semaforo/reconectar');
      if (r.data.conectado) toast.success('Conectado ao broker.');
      else toast.error(`Sem conexão com o broker: ${r.data.motivo || 'verifique o endereço, o usuário e a senha'}`, { duration: 8000 });
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível reconectar');
    } finally {
      setReconectando(false);
    }
  };

  if (falhou) return <div className="smf-bloco smf-aviso" role="alert">Não foi possível ler o estado do semáforo. Recarregue a página.</div>;
  if (!estado) return <div className="smf-bloco smf-carregando">Consultando o broker…</div>;

  const tom = !estado.configurado ? 'neutro' : estado.conectado ? 'ok' : 'atencao';
  const rotulo = !estado.configurado ? 'Sem broker configurado' : estado.conectado ? 'Conectado ao broker' : 'Sem conexão com o broker';

  return (
    <div className="smf-bloco" role="status">
      <div className={`smf-estado smf-${tom}`}>
        <span className="smf-rotulo">{rotulo}</span>
        {estado.configurado && <span className="smf-texto">{estado.url} · usuário {estado.usuario || '(nenhum)'} · senha {estado.senha_definida ? 'definida' : 'NÃO definida'}</span>}
        {!estado.conectado && estado.ultimo_erro && <span className="smf-texto">Último erro: {estado.ultimo_erro}</span>}
        {estado.configurado && (
          <span className="smf-texto">
            Sinais enviados desde o arranque: {estado.enviados.verde} verde, {estado.enviados.vermelho} vermelho, {estado.enviados.falhas} sem confirmação
            {estado.enviados.ultimo && <> · último: {estado.enviados.ultimo.cor} {estado.enviados.ultimo.placa} em {estado.enviados.ultimo.camera} ({haQuanto(estado.enviados.ultimo.em)})</>}
          </span>
        )}
      </div>

      <div className="smf-linha">
        <label className="smf-rotulo" htmlFor="smf-senha">Senha do broker</label>
        <input
          id="smf-senha" type="password" autoComplete="new-password" value={senha}
          onChange={(e) => setSenha(e.target.value)} placeholder={estado.senha_definida ? '•••••••• (definida; digite para trocar)' : 'digite a senha do usuário da plataforma'}
        />
        <div className="smf-acoes">
          <button type="button" className="btn btn-sm btn-primary" onClick={gravarSenha} disabled={gravando}>
            {gravando ? 'Gravando…' : (senha ? 'Gravar senha' : 'Apagar senha')}
          </button>
          <button type="button" className="btn btn-sm btn-info" onClick={reconectar} disabled={reconectando || !estado.configurado}>
            {reconectando ? 'Conectando…' : 'Reconectar'}
          </button>
        </div>
      </div>

      <div className="smf-linha">
        <label className="smf-rotulo" htmlFor="smf-senha-ctrl">Senha da controladora</label>
        <input
          id="smf-senha-ctrl" type="password" autoComplete="new-password" value={senhaControladora}
          onChange={(e) => setSenhaControladora(e.target.value)}
          placeholder={estado.senha_controladora_definida ? '•••••••• (definida; digite para trocar)' : 'a senha do usuário da controladora no broker (= código dela)'}
        />
        <div className="smf-acoes">
          <button type="button" className="btn btn-sm btn-primary" onClick={gravarSenhaControladora} disabled={gravandoControladora}>
            {gravandoControladora ? 'Gravando…' : (senhaControladora ? 'Gravar senha' : 'Apagar senha')}
          </button>
        </div>
        <span className="smf-texto">Vai no config.h do firmware baixado na Central de Ajuda. No broker, o usuário de cada controladora é o próprio código dela.</span>
      </div>

      <div className="smf-controladoras">
        <span className="smf-rotulo">Controladoras</span>
        {estado.cameras.length === 0 ? (
          <span className="smf-texto">Nenhuma câmera tem controladora cadastrada. Edite a câmera na tela Câmeras e preencha "Semáforo da guarita".</span>
        ) : (
          <ul className="smf-lista">
            {estado.cameras.map((c) => (
              <li key={c.camera_id} className={c.online ? 'smf-online' : 'smf-offline'}>
                <strong>{c.codigo || '(sem código)'}</strong> · {c.camera} · {c.ligado ? 'ligado' : 'desligado'} ·{' '}
                {c.online ? `viva (${haQuanto(c.visto_em)}` : `sem sinal (último ${haQuanto(c.visto_em)}`}
                {c.ip ? `, ${c.ip}` : ''}{c.firmware ? `, firmware ${c.firmware}` : ''})
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Onda 179: as controladoras de liberação de saída, pela mesma árvore e o mesmo heartbeat. */}
      {Array.isArray(estado.saida) && estado.saida.length > 0 && (
        <div className="smf-controladoras">
          <span className="smf-rotulo">Controladoras de saída</span>
          <ul className="smf-lista">
            {estado.saida.map((c) => (
              <li key={`saida-${c.camera_id}`} className={c.online ? 'smf-online' : 'smf-offline'}>
                <strong>{c.codigo}</strong> · relé {c.rele || '?'} → {c.camera} · pulso {c.pulso_ms} ms ·{' '}
                {c.online ? `viva (${haQuanto(c.visto_em)}` : `sem sinal (último ${haQuanto(c.visto_em)}`}
                {c.ip ? `, ${c.ip}` : ''}{c.firmware ? `, firmware ${c.firmware}` : ''}{Number.isFinite(c.aberturas) ? `, ${c.aberturas} abertura(s)` : ''})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
